// GAVION booking: the real PublicBookingApi: gavion-booking over fetch. No credential is ever sent (the function is
// public). A manage token only ever travels in a POST body (Task 4 ruling 1): never in a URL, never logged.
import {
  type ManageRep,
  type ManageView,
  type PublicBookingApi,
  PublicBookingError,
  type PublicRep,
} from "./types";

type Obj = Record<string, unknown>;
const SAFE = /^[a-z_]{1,40}$/;
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : []);
const repOf = (v: unknown): PublicRep => {
  const o = (v ?? {}) as Obj;
  return { firstName: str(o.first_name), lastName: str(o.last_name), avatarUrl: str(o.avatar_url) || null };
};
const manageRepOf = (v: unknown): ManageRep | null => {
  if (!v || typeof v !== "object") return null;
  const o = v as Obj;
  return { firstName: str(o.first_name), lastName: str(o.last_name), email: str(o.email) };
};
const viewOf = (o: Obj): ManageView => ({
  status: (str(o.status) || "booked") as ManageView["status"],
  startsAt: str(o.starts_at),
  endsAt: str(o.ends_at),
  language: o.language === "en" ? "en" : "fr",
  rep: manageRepOf(o.rep) ?? { firstName: "", lastName: "", email: "" },
});

export function createHttpPublicBookingApi(
  base: string,
  f: typeof fetch = (input, init) => fetch(input, init),
): PublicBookingApi {
  const call = async (path: string, init?: RequestInit): Promise<Obj> => {
    let res: Response;
    try {
      res = await f(`${base}${path}`, init);
    } catch {
      throw new PublicBookingError("network");
    }
    const body = (await res.json().catch(() => ({}))) as Obj;
    if (res.ok) return body;
    const code = SAFE.test(str(body.error)) ? str(body.error) : "error";
    throw new PublicBookingError(code, { slots: strings(body.slots), rep: manageRepOf(body.rep) });
  };
  const postJson = (path: string, body: unknown) =>
    call(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  const pageOf = (b: Obj) => ({
    rep: repOf(b.rep),
    slots: strings(b.slots),
    horizonEnd: str(b.horizon_end),
    nextFrom: str(b.next_from) || null,
  });
  return {
    async slots(q) {
      if ("token" in q) return pageOf(await postJson("/slots", { token: q.token, from: q.from, to: q.to }));
      const p = new URLSearchParams({ slug: q.slug, from: q.from, to: q.to });
      return pageOf(await call(`/slots?${p.toString()}`));
    },
    async book(i) {
      const b = await postJson("/book", i);
      return {
        status: b.status === "rescheduled" ? "rescheduled" : b.status === "received" ? "received" : "booked",
        startsAt: str(b.starts_at) || null,
        endsAt: str(b.ends_at) || null,
        manageToken: str(b.manage_token) || null,
      };
    },
    manage: async (token) => viewOf(await postJson("/manage", { token })),
    reschedule: async (token, start) => viewOf(await postJson("/manage/reschedule", { token, start })),
    cancel: async (token) => viewOf(await postJson("/manage/cancel", { token })),
  };
}
